/// <reference types="vite/client" />
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties, DragEvent } from "react";
import { ErrorBanner } from "../components/ErrorBanner";
import { LeadDrawer } from "../components/LeadDrawer";
import { useEngagement } from "../hooks/useEngagement";

// ─── Wire-format ──────────────────────────────────────────────────────

interface EmailFunnelState {
  sent: boolean;
  received: boolean;
  opens: number;
  clicks: number;
}

interface PipelineLead {
  id: string;
  companyName: string;
  status: string;
  bantScore: number | null;
  contactEmail: string | null;
  source: string;
  updatedAt: string;
  emailFunnel?: EmailFunnelState;
}

interface PipelineSnapshot {
  leads: PipelineLead[];
  opportunities: Array<{ id: string; leadId: string; stage: string; estimatedValueCents: number | null; updatedAt: string }>;
}

// Column order from the issue. Empty columns still render so cards have
// a target to be dropped on.
const COLUMNS: Array<{ id: LeadStatus; label: string; code: string }> = [
  { id: "new",          label: "New",          code: "01" },
  { id: "needs_review", label: "Needs Review", code: "02" },
  { id: "contacted",    label: "Contacted",    code: "03" },
  { id: "responded",    label: "Responded",    code: "04" },
  { id: "qualified",    label: "Qualified",    code: "05" },
  { id: "won",          label: "Won",          code: "06" },
  { id: "lost",         label: "Lost",         code: "07" },
];

type LeadStatus = "new" | "needs_review" | "contacted" | "responded" | "qualified" | "won" | "lost";

function bantColor(score: number | null): string {
  if (score == null) return "var(--color-text-dim)";
  if (score >= 70) return "var(--color-status-active)";
  if (score >= 55) return "var(--color-accent-bright)";
  return "var(--color-text-dim)";
}

function bantBorder(score: number | null): string {
  if (score == null) return "rgba(120,113,108,0.3)";
  if (score >= 70) return "rgba(132,204,22,0.4)";
  if (score >= 55) return "rgba(139,92,246,0.4)";
  return "rgba(120,113,108,0.3)";
}

function bantBg(score: number | null): string {
  if (score == null) return "rgba(120,113,108,0.06)";
  if (score >= 70) return "rgba(132,204,22,0.08)";
  if (score >= 55) return "rgba(139,92,246,0.08)";
  return "rgba(120,113,108,0.06)";
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

// ─── Page ─────────────────────────────────────────────────────────────

export function CRM() {
  const { engagementId: engagement } = useEngagement();
  const [leadsByStatus, setLeadsByStatus] = useState<Record<string, PipelineLead[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [drawerLeadId, setDrawerLeadId] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      const qs = engagement ? `?engagement=${encodeURIComponent(engagement)}` : "";
      fetch(`/api/crm/pipeline${qs}`, { signal })
        .then(async (res) => {
          if (!res.ok) {
            const body = await res.text().catch(() => "");
            throw new Error(`HTTP ${res.status}${body ? ` — ${body.slice(0, 200)}` : ""}`);
          }
          return res.json() as Promise<PipelineSnapshot>;
        })
        .then((json) => {
          const grouped: Record<string, PipelineLead[]> = {};
          for (const col of COLUMNS) grouped[col.id] = [];
          for (const lead of json.leads) {
            if (!grouped[lead.status]) grouped[lead.status] = [];
            grouped[lead.status].push(lead);
          }
          setLeadsByStatus(grouped);
          setLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn("[CRM] /api/crm/pipeline fetch failed.", err);
          }
          setError(err.message);
          setLoading(false);
        });
    },
    [engagement],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const onDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>, toStatus: LeadStatus) => {
      e.preventDefault();
      setDragOver(null);
      const leadId = e.dataTransfer.getData("text/lead-id");
      const fromStatus = e.dataTransfer.getData("text/from-status");
      if (!leadId || !fromStatus || fromStatus === toStatus) return;

      // Optimistic move — snapshot the prior state for rollback.
      const previous = leadsByStatus;
      setLeadsByStatus((prev) => {
        const next = { ...prev };
        const fromCol = (next[fromStatus] ?? []).filter((l) => l.id !== leadId);
        const moved = (prev[fromStatus] ?? []).find((l) => l.id === leadId);
        if (!moved) return prev;
        next[fromStatus] = fromCol;
        next[toStatus] = [{ ...moved, status: toStatus, updatedAt: new Date().toISOString() }, ...(next[toStatus] ?? [])];
        return next;
      });

      try {
        const res = await fetch("/api/crm/transition", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entity_type: "lead", entity_id: leadId, to_status: toStatus }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { message?: string };
          throw new Error(body.message ?? `HTTP ${res.status}`);
        }
        setTransitionError(null);
      } catch (err) {
        // Rollback.
        setLeadsByStatus(previous);
        setTransitionError(err instanceof Error ? err.message : String(err));
      }
    },
    [leadsByStatus],
  );

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>, status: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver(status);
  }, []);

  const totalLeads = useMemo(
    () => Object.values(leadsByStatus).reduce((acc, arr) => acc + arr.length, 0),
    [leadsByStatus],
  );

  if (error && totalLeads === 0) {
    return <ErrorBanner message={error} onRetry={load} context="CRM pipeline" />;
  }

  return (
    <div className="space-y-6 lg:space-y-8">
      {/* Page header — engagement is now driven by the global picker (Y5). */}
      <header className="reveal" style={{ animationDelay: "0ms" }}>
        <p className="eyebrow">Console · 04</p>
        <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
          <span className="text-[var(--color-text)]">CRM </span>
          <span className="text-accent-gradient">Pipeline</span>
        </h2>
        <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
          Tap a lead to open the detail drawer. On desktop, drag between columns to advance the pipeline ; each move is audited.
        </p>
      </header>

      {transitionError && (
        <div
          className="panel p-4 reveal flex items-start gap-3"
          style={{ borderColor: "var(--color-status-error)", background: "rgba(239,68,68,0.04)" }}
        >
          <span className="mt-1 inline-block w-2 h-2 rounded-full" style={{ background: "var(--color-status-error)" }} />
          <div className="flex-1">
            <p className="eyebrow" style={{ color: "var(--color-status-error)" }}>Transition rolled back</p>
            <p className="font-mono text-xs mt-1 text-[var(--color-text-muted)]">{transitionError}</p>
          </div>
          <button
            onClick={() => setTransitionError(null)}
            className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          >
            Dismiss
          </button>
        </div>
      )}

      {loading && totalLeads === 0 && (
        <div className="flex items-center gap-3 text-[var(--color-text-dim)]">
          <span className="live-dot text-[var(--color-accent)]" />
          <span className="font-mono text-xs tracking-[0.18em] uppercase blink">Loading pipeline…</span>
        </div>
      )}

      {/* Mobile-only swipe hint */}
      <p className="md:hidden font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
        ← swipe →
      </p>

      {/* Kanban — column-by-column swipe on mobile (snap-mandatory), free scroll on tablet+. */}
      <section className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 -mx-4 sm:-mx-2 px-4 sm:px-2 snap-x snap-mandatory md:snap-none">
        {COLUMNS.map((col, i) => {
          const cards = leadsByStatus[col.id] ?? [];
          const isOver = dragOver === col.id;
          return (
            <div
              key={col.id}
              onDragOver={(e) => onDragOver(e, col.id)}
              onDragLeave={() => setDragOver((s) => (s === col.id ? null : s))}
              onDrop={(e) => onDrop(e, col.id)}
              className="reveal shrink-0 w-[85vw] sm:w-[60vw] md:w-[280px] snap-center md:snap-start rounded-xl flex flex-col"
              style={{
                animationDelay: `${80 + i * 60}ms`,
                background: "var(--color-bg-elev)",
                border: `1px solid ${isOver ? "var(--color-accent)" : "var(--color-border)"}`,
                boxShadow: isOver ? "0 0 0 1px var(--color-accent), 0 0 30px -10px var(--color-accent-glow)" : undefined,
                transition: "border-color 200ms ease, box-shadow 200ms ease",
                minHeight: "60vh",
              } as CSSProperties}
            >
              <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b border-[var(--color-border)]">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] text-[var(--color-text-dim)]">{col.code}</span>
                  <h3 className="font-display italic text-xl text-[var(--color-text)]">{col.label}</h3>
                </div>
                <span className="font-mono text-[10px] text-[var(--color-text-dim)] tabular-nums">
                  {cards.length}
                </span>
              </div>

              <div className="flex-1 p-3 space-y-2 overflow-y-auto">
                {cards.length === 0 ? (
                  <p className="font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)] text-center py-6">
                    No leads here yet
                  </p>
                ) : (
                  cards.map((lead) => (
                    <LeadCard
                      key={lead.id}
                      lead={lead}
                      fromStatus={col.id}
                      onClick={() => setDrawerLeadId(lead.id)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </section>

      <LeadDrawer
        leadId={drawerLeadId}
        onClose={() => setDrawerLeadId(null)}
        onTransitioned={() => load()}
      />
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────

function LeadCard({
  lead,
  fromStatus,
  onClick,
}: {
  lead: PipelineLead;
  fromStatus: string;
  onClick: () => void;
}) {
  const handleDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/lead-id", lead.id);
      e.dataTransfer.setData("text/from-status", fromStatus);
    },
    [lead.id, fromStatus],
  );

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onClick={onClick}
      className="panel p-3 cursor-pointer select-none"
      style={{ background: "var(--color-bg-card)" }}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-display italic text-base text-[var(--color-text)] leading-tight truncate">
          {lead.companyName}
        </h4>
        <span
          className="pill shrink-0"
          style={{
            color: bantColor(lead.bantScore),
            borderColor: bantBorder(lead.bantScore),
            background: bantBg(lead.bantScore),
          }}
        >
          {lead.bantScore != null ? `BANT ${lead.bantScore}` : "BANT —"}
        </span>
      </div>

      {lead.contactEmail && (
        <p className="mt-2 font-mono text-[11px] text-[var(--color-text-muted)] truncate">
          {lead.contactEmail}
        </p>
      )}

      {lead.emailFunnel && <EmailFunnelStrip funnel={lead.emailFunnel} />}

      <div className="mt-3 flex items-center justify-between font-mono text-[10px] tracking-wider uppercase">
        <span className="text-[var(--color-text-dim)]">{lead.source}</span>
        <span className="text-[var(--color-text-dim)] tabular-nums normal-case tracking-normal">
          {formatTimestamp(lead.updatedAt)}
        </span>
      </div>
    </div>
  );
}

/**
 * Compact 4-stage funnel rendered on each kanban card so operators
 * can see at a glance who's at which stage of the audit-email
 * funnel without opening the drawer. Filled dot = stage reached.
 */
function EmailFunnelStrip({ funnel }: { funnel: EmailFunnelState }) {
  const opensLabel = funnel.opens > 1 ? `Ouvert ${funnel.opens}x` : "Ouvert";
  const clicksLabel = funnel.clicks > 1 ? `Cliqué ${funnel.clicks}x` : "Cliqué";

  const steps: Array<{ key: string; label: string; reached: boolean }> = [
    { key: "sent", label: "Envoyé", reached: funnel.sent },
    { key: "received", label: "Reçu", reached: funnel.received },
    { key: "opened", label: opensLabel, reached: funnel.opens > 0 },
    { key: "clicked", label: clicksLabel, reached: funnel.clicks > 0 },
  ];

  return (
    <div className="mt-2.5 flex items-center gap-1.5">
      {steps.map((s) => (
        <span
          key={s.key}
          title={`${s.label} · ${s.reached ? "atteint" : "non atteint"}`}
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{
            background: s.reached ? "var(--color-accent-bright)" : "transparent",
            border: s.reached
              ? "1px solid var(--color-accent)"
              : "1px solid var(--color-border)",
            boxShadow: s.reached
              ? "0 0 6px color-mix(in srgb, var(--color-accent) 50%, transparent)"
              : "none",
          }}
        />
      ))}
    </div>
  );
}
